const API_BASE_URL = "https://moncopieur.cloud-erp.ma";

// Types from OpenAPI schema
export interface PartnerSelf {
  id: number;
  name: string;
  company_type: 'person' | 'company';
  street?: string;
  city?: string;
  zip?: string;
  country_id?: number | null;
  country_name?: string | null;
  state_id?: number | null;
  state_name?: string | null;
  phone?: string;
  email: string;
  mobile?: string;
  vat?: string | null;
  company_registry?: string | null;
}

export interface UpdatePartnerRequest {
  name?: string;
  company_type?: 'person' | 'company';
  street?: string;
  city?: string;
  zip?: string;
  zip_code?: string; // Alias for zip
  country_id?: number;
  state_id?: number;
  phone?: string;
  mobile?: string;
  vat?: string;
  company_registry?: string;
}

export interface ChangePasswordRequest {
  old_password: string;
  new_password: string;
  confirm_password: string;
  revoke_sessions?: boolean;
}

export interface DeliveryAddress {
  id: number;
  name: string;
  street: string;
  city: string;
  state_id: number;
  state_name: string;
  zip_code: string;
  country_id: number;
  country_name: string;
  partner_latitude: number;
  partner_longitude: number;
}

export interface DeliveryAddressCreateRequest {
  name: string;
  street: string;
  city: string;
  state_id: number;
  zip_code: string;
  country_id: number;
  partner_latitude: number;
  partner_longitude: number;
}

export interface ApiError {
  success: false;
  error: string;
}

export interface BasicSuccess {
  success: true;
  message: string;
}

export interface PartnerResponse {
  success: true;
  partner: PartnerSelf;
}

export interface DeliveryAddressListResponse {
  success: true;
  items: DeliveryAddress[];
}

export interface DeliveryAddressCreateResponse {
  success: true;
  message: string;
  address: {
    id: number;
    name: string;
  };
}

// Helper function to make authenticated requests
const makeAuthenticatedRequest = async (
  endpoint: string, 
  options: RequestInit = {},
  token: string
) => {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || `HTTP ${response.status}`);
  }
  
  return data;
};

// Partner API functions
export const getPartnerProfile = async (token: string): Promise<PartnerResponse> => {
  return makeAuthenticatedRequest('/ws/partner/me', { method: 'GET' }, token);
};

export const updatePartnerProfile = async (
  token: string, 
  data: UpdatePartnerRequest
): Promise<BasicSuccess> => {
  return makeAuthenticatedRequest('/ws/partner/me', {
    method: 'POST',
    body: JSON.stringify(data),
  }, token);
};

export const changePassword = async (
  token: string, 
  data: ChangePasswordRequest
): Promise<BasicSuccess> => {
  return makeAuthenticatedRequest('/ws/partner/change_password', {
    method: 'POST',
    body: JSON.stringify(data),
  }, token);
};

export const getDeliveryAddresses = async (token: string): Promise<DeliveryAddressListResponse> => {
  return makeAuthenticatedRequest('/ws/partner/delivery_addresses', { method: 'GET' }, token);
};

export const createDeliveryAddress = async (
  token: string, 
  data: DeliveryAddressCreateRequest
): Promise<DeliveryAddressCreateResponse> => {
  return makeAuthenticatedRequest('/ws/partner/delivery_addresses', {
    method: 'POST',
    body: JSON.stringify(data),
  }, token);
};

export const deleteInvoiceAddress = async (
  token: string, 
  addressId: number
): Promise<BasicSuccess> => {
  return makeAuthenticatedRequest('/ws/partner/invoice_address/delete', {
    method: 'POST',
    body: JSON.stringify({ address_id: addressId }),
  }, token);
};

export const deleteDeliveryAddress = async (
  token: string, 
  addressId: number
): Promise<BasicSuccess> => {
  return makeAuthenticatedRequest('/ws/partner/delivery_address/delete', {
    method: 'POST',
    body: JSON.stringify({ address_id: addressId }),
  }, token);
};